/**
 * Análises — inteligência preditiva e operacional (NÃO é cópia do dashboard).
 *
 * Fonte: GET /device-monitoring/analytics/overview. Todo número vem de
 * dados persistidos; sem histórico mínimo a tela diz "insuficiente".
 * RISCO PREDITIVO (chance de nova atividade) ≠ CLASSIFICAÇÃO OPERACIONAL
 * (resultado da inspeção) — um dispositivo intacto hoje pode ter risco alto.
 */
import { useCallback, useEffect, useState } from "react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage, typeLabel } from "./hierarchy";
import {
  Badge, Card, ClassBadge, DeviceDetail, Empty, GlobalFilters, NEUTRAL, RISK_INFO, STATUS,
  filtersToParams, fmtDay,
} from "./viz";

const TREND = {
  CRESCENTE: { label: "Crescente", color: STATUS.critical, icon: "↗" },
  ESTAVEL: { label: "Estável", color: NEUTRAL, icon: "→" },
  DECRESCENTE: { label: "Decrescente", color: STATUS.good, icon: "↘" },
  INSUFICIENTE: { label: "Histórico insuficiente", color: NEUTRAL, icon: "·" },
};
const CONF = { BAIXA: "baixa", MEDIA: "média", ALTA: "alta" };

function Spark({ values, color = NEUTRAL }) {
  if (!values?.length) return null;
  return (
    <div className="h-8 w-28">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={values.map((v, i) => ({ i, v }))}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function RiskBar({ score, level }) {
  if (score === null || score === undefined) return <span className="text-xs text-muted-foreground">insuficiente</span>;
  const info = RISK_INFO[level] || {};
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="h-2 flex-1 rounded bg-secondary overflow-hidden">
        <div className="h-full rounded-r" style={{ width: `${score}%`, background: info.color }} />
      </div>
      <span className="text-xs font-medium w-14 text-right">{Math.round(score)} · {info.label}</span>
    </div>
  );
}

export default function Analysis({ filters, setFilters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState(null);
  const closeDevice = useCallback(() => setDevice(null), []);

  useEffect(() => {
    if (filters.period === "personalizado" && (!filters.date_from || !filters.date_to)) return;
    let current = true;
    setLoading(true);
    api.get("/device-monitoring/analytics/overview", { params: filtersToParams(filters) })
      .then(({ data: d }) => current && setData(d))
      .catch((err) => current && toast.error(apiErrorMessage(err, "Erro ao carregar análises.")))
      .finally(() => current && setLoading(false));
    return () => { current = false; };
  }, [filters]);

  const dist = data?.risk_now?.distribution || [];
  return (
    <div className="space-y-4" data-testid="dm-analysis">
      <GlobalFilters value={filters} onChange={setFilters} />
      {loading && !data ? <div className="py-16 text-center text-muted-foreground">Carregando…</div> : !data ? null : (
        <>
          {!data.sufficiency?.sufficient && (
            <div className="rounded-md border border-border p-3 text-sm font-medium" data-testid="dm-insufficient">
              {data.sufficiency?.message || "DADOS HISTÓRICOS INSUFICIENTES PARA PREVISÃO."}
              <span className="block text-xs font-normal text-muted-foreground">
                {data.sufficiency?.inspections ?? 0} inspeções · {data.sufficiency?.weeks ?? 0} semana(s) de histórico ·
                mínimo {data.methodology?.min_history ?? 3} observações por item.
              </span>
            </div>
          )}

          {/* RISCO ATUAL */}
          <Card title="Risco atual" subtitle="Risco preditivo de nova atividade por dispositivo (0–100)">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {dist.map((d) => (
                <div key={d.level} className="rounded-md border border-border p-3">
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <span aria-hidden style={{ color: RISK_INFO[d.level]?.color }}>●</span>{RISK_INFO[d.level]?.label || d.label}
                  </div>
                  <div className="text-2xl font-semibold">{d.count}</div>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Áreas com maior risco" subtitle="Atividade, reincidência, criticidade e tendência">
              {!data.top_areas_risk?.length ? <Empty text="Sem dados por área" /> : (
                <ul className="space-y-3">
                  {data.top_areas_risk.map((a) => (
                    <li key={a.area_id} className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium">{a.area_name}</span>
                        <Badge info={TREND[a.trend]}>{TREND[a.trend]?.label}{a.trend_confidence ? ` (${CONF[a.trend_confidence]})` : ""}</Badge>
                        <span className="ml-auto"><Spark values={a.weekly_activity} color={TREND[a.trend]?.color} /></span>
                      </div>
                      <RiskBar score={a.score} level={a.level} />
                      <div className="text-xs text-muted-foreground">{(a.reasons || []).join(" · ")}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title="Dispositivos com maior risco" subtitle="Score explicável — clique para ver motivos e linha do tempo">
              {!data.top_devices_risk?.length ? <Empty text="Histórico insuficiente para pontuar dispositivos" /> : (
                <div className="overflow-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                      <tr><th className="text-left py-1.5 px-2">Dispositivo</th><th className="text-left py-1.5 px-2">Hoje</th>
                        <th className="text-left py-1.5 px-2">Risco</th><th className="text-left py-1.5 px-2">Motivos</th></tr>
                    </thead>
                    <tbody>
                      {data.top_devices_risk.map((d) => (
                        <tr key={d.device_id} className="border-t border-border/50 align-top">
                          <td className="py-1.5 px-2">
                            <button className="underline font-mono" onClick={() => setDevice(d.device_id)}>{d.code}</button>
                            <div className="text-muted-foreground">{d.area_name} · {typeLabel(d.type_key)}</div>
                          </td>
                          <td className="py-1.5 px-2"><ClassBadge cls={d.current_classification} /></td>
                          <td className="py-1.5 px-2"><RiskBar score={d.score} level={d.level} /></td>
                          <td className="py-1.5 px-2 text-muted-foreground">{(d.reasons || []).slice(0, 3).join("; ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* TENDÊNCIAS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Setores: melhorando ou piorando?" subtitle="Inspeções com atividade por semana (regressão + tau de Kendall)">
              {!data.trends?.sectors?.length ? <Empty /> : (
                <ul className="divide-y divide-border/50">
                  {data.trends.sectors.map((s) => (
                    <li key={s.area_id} className="flex items-center gap-2 py-1.5 text-sm">
                      <span>{s.area_name}</span>
                      <Badge info={TREND[s.trend]}>{TREND[s.trend]?.label}{s.confidence ? ` · confiança ${CONF[s.confidence]}` : ""}</Badge>
                      <span className="ml-auto"><Spark values={s.series} color={TREND[s.trend]?.color} /></span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title="Espécies: período atual × anterior">
              {!data.trends?.species?.length ? <Empty text="Sem capturas por espécie" /> : (
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground"><tr>
                    <th className="text-left py-1.5 px-2">Espécie</th><th className="text-right py-1.5 px-2">Anterior</th>
                    <th className="text-right py-1.5 px-2">Atual</th><th className="text-right py-1.5 px-2">Variação</th>
                    <th className="text-left py-1.5 px-2">Tendência</th></tr></thead>
                  <tbody>
                    {data.trends.species.map((s) => (
                      <tr key={s.species_id} className="border-t border-border/50">
                        <td className="py-1.5 px-2">{s.name}</td>
                        <td className="py-1.5 px-2 text-right">{s.previous}</td>
                        <td className="py-1.5 px-2 text-right font-medium">{s.current}</td>
                        <td className="py-1.5 px-2 text-right">{s.pct === null ? "sem base" : `${s.pct > 0 ? "+" : ""}${s.pct}%`}</td>
                        <td className="py-1.5 px-2"><Badge info={TREND[s.trend?.direction]}>{TREND[s.trend?.direction]?.label}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          {/* REINCIDÊNCIAS + ANOMALIAS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Reincidências" subtitle={`Regra: ≥ ${data.methodology?.recurrence?.min_events} eventos nas últimas ${data.methodology?.recurrence?.window} inspeções`}>
              {!data.recurrences?.length ? <Empty text="Nenhum dispositivo reincidente" /> : (
                <ul className="space-y-1.5 text-sm">
                  {data.recurrences.map((r) => (
                    <li key={r.device_id} className="flex flex-wrap items-center gap-2">
                      <button className="underline font-mono" onClick={() => setDevice(r.device_id)}>{r.code}</button>
                      <span className="text-xs text-muted-foreground">{r.area_name}</span>
                      <Badge info={{ color: r.level === "ALTA_REINCIDENCIA" ? STATUS.critical : STATUS.serious, icon: "▲" }}>{r.label}</Badge>
                      <span className="text-xs text-muted-foreground ml-auto">{r.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title="Anomalias" subtitle="Aumento atípico contra a média histórica do próprio dispositivo">
              {!data.anomalies?.length ? <Empty text="Nenhuma anomalia detectada (ou histórico insuficiente)" /> : (
                <ul className="space-y-2 text-sm">
                  {data.anomalies.map((a) => (
                    <li key={a.device_id} className="rounded-md border border-border p-2">
                      <div className="flex items-center gap-2">
                        <button className="underline font-mono" onClick={() => setDevice(a.device_id)}>{a.code}</button>
                        <span className="text-xs text-muted-foreground">{typeLabel(a.type_key)} · {a.area_name} · {fmtDay(a.date)}</span>
                        <Badge info={{ color: STATUS.critical, icon: "▲" }}>Aumento atípico</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Média histórica {a.mean} {a.unit} · atual {a.current} {a.unit} ·{" "}
                        {a.pct_change === null ? "primeira atividade (média 0)" : `variação ${a.pct_change > 0 ? "+" : ""}${a.pct_change}%`}
                        {a.z !== null ? ` · z = ${a.z}` : ""} ({a.n} leituras)
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {/* PREVISÃO */}
          <Card title="Previsão — próxima semana" subtitle="Método e confiança calculados (sem percentuais fictícios)">
            {!data.predictions?.length ? <Empty text="DADOS HISTÓRICOS INSUFICIENTES PARA PREVISÃO." /> : (
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground"><tr>
                    <th className="text-left py-1.5 px-2">Escopo</th><th className="text-left py-1.5 px-2">Métrica</th>
                    <th className="text-left py-1.5 px-2">Histórico</th><th className="text-right py-1.5 px-2">Previsão</th>
                    <th className="text-left py-1.5 px-2">Intervalo</th><th className="text-left py-1.5 px-2">Método</th>
                    <th className="text-left py-1.5 px-2">Confiança</th></tr></thead>
                  <tbody>
                    {data.predictions.map((p, i) => (
                      <tr key={`${p.name}-${i}`} className="border-t border-border/50">
                        <td className="py-1.5 px-2">{p.scope === "tipo" ? typeLabel(p.name) : p.name}</td>
                        <td className="py-1.5 px-2 text-muted-foreground">{p.metric}</td>
                        <td className="py-1.5 px-2"><Spark values={p.history} /></td>
                        {p.sufficient ? (
                          <>
                            <td className="py-1.5 px-2 text-right font-medium">{p.value}</td>
                            <td className="py-1.5 px-2">{p.low} – {p.high}</td>
                            <td className="py-1.5 px-2">{p.method}</td>
                            <td className="py-1.5 px-2">{CONF[p.confidence]}</td>
                          </>
                        ) : (
                          <td className="py-1.5 px-2 text-muted-foreground" colSpan={4}>{p.label}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* INSIGHTS + RECOMENDAÇÕES */}
          <Card title="Insights e recomendações" subtitle="DADO → INSIGHT → RECOMENDAÇÃO">
            {!data.insights?.length ? <Empty text="Nenhum insight: não há evidência suficiente no recorte" /> : (
              <ul className="space-y-2">
                {data.insights.map((ins, i) => (
                  <li key={i} className="rounded-md border border-border p-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    <div><div className="text-xs text-muted-foreground">Dado</div>{ins.data}</div>
                    <div><div className="text-xs text-muted-foreground">Insight</div>
                      <span aria-hidden style={{ color: ins.severity === "alta" ? STATUS.critical : STATUS.warning }}>▲ </span>{ins.insight}</div>
                    <div><div className="text-xs text-muted-foreground">Recomendação</div>{ins.recommendation}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <details className="rounded-lg border border-border bg-card p-3 text-xs">
            <summary className="cursor-pointer font-medium">Metodologia (configurável no backend)</summary>
            <ul className="mt-2 space-y-1 text-muted-foreground">
              <li>Histórico mínimo: {data.methodology?.min_history} observações (preliminar até {(data.methodology?.robust_history || 6) - 1}; robusto a partir de {data.methodology?.robust_history}).</li>
              <li>Pesos do risco do dispositivo: {Object.entries(data.methodology?.risk_weights || {}).map(([k, v]) => `${k} ${v}`).join(", ")}.</li>
              <li>Faixas: {(data.methodology?.risk_bands || []).map((b) => `${b.label} ≥ ${b.from}`).join(" · ")}.</li>
              <li>Tendência: {data.methodology?.trend}.</li>
              <li>Anomalia: {data.methodology?.anomaly}.</li>
              <li>Previsão: {data.methodology?.forecast}.</li>
              <li>Confiança: {data.methodology?.confidence}.</li>
            </ul>
          </details>
        </>
      )}
      {device && <DeviceDetail deviceId={device} onClose={closeDevice} />}
    </div>
  );
}
